import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { mdiAlertOutline, mdiOpenInNew, mdiPencilOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useChildAssets, useFolderLayouts, useUpdateAsset, refreshAssetDocumentFolders } from '../../hooks/useQueries'
import { getLocalizedName } from '../../lib/localization'

/**
 * Value used by the `Select` for the "inherit / no layout" choice. Radix's
 * Select forbids an empty-string item value, so the sentinel is explicit and
 * mapped back to `null` on submit (which the API reads as "clear the column",
 * see internal/handlers/assets/service.go — NullableUUID).
 */
const NONE_VALUE = '__none__'

interface AssetFolderLayoutDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    org: string
    /**
     * The *site* asset the layout is written on — never a descendant. This is
     * the identifier the documents tab was routed with (a UUID or the
     * `sourceCode:identifier` external-ID form), so it is also the key the
     * `document-folders` query is cached under and must be invalidated with.
     */
    assetUid: string
    /**
     * Same asset, but its canonical UUID (`site_asset_uid` from the folder
     * payload). The children lookup filters on `parent_asset_uid`, which only
     * accepts a UUID — `assetUid` may be an external-ID form.
     */
    siteAssetUuid?: string
    /**
     * The layout currently set on this asset's own column (not the resolved /
     * inherited one). Undefined → the dialog opens on "None (inherit)".
     */
    currentLayoutUid?: string | null
}

/**
 * Picks the document folder layout of a *site* asset (spec 2026-07-30-01).
 *
 * A plain `Dialog`, not an `AlertDialog`: this is a form, not a confirmation.
 * Editing the folder structure itself stays on `/$org/folder-layouts`, which
 * the footer links out to rather than duplicating.
 */
export function AssetFolderLayoutDialog({
    open,
    onOpenChange,
    org,
    assetUid,
    siteAssetUuid,
    currentLayoutUid,
}: AssetFolderLayoutDialogProps) {
    const { t, i18n } = useTranslation('folders')
    const queryClient = useQueryClient()
    const [selected, setSelected] = useState<string>(NONE_VALUE)
    const [error, setError] = useState<string | null>(null)

    // Only fetch the org's layouts / the children lookup while the dialog is
    // actually open — this component is mounted on every site documents tab.
    // Only asset-targeted layouts may be attached to an asset (spec
    // 2026-08-13-01 §3) — the backend answers 422 otherwise, so the picker
    // must not offer the others.
    const { data: layouts, isLoading } = useFolderLayouts(org, open, 'asset')
    const { data: children } = useChildAssets(org, siteAssetUuid, open)
    const hasDescendants = children.length > 0
    const updateAsset = useUpdateAsset(org, assetUid)

    // Re-sync the selection whenever the dialog (re)opens or the asset's own
    // layout changes underneath — never derive it from the *resolved* layout,
    // so an asset inheriting from an ancestor opens on "None".
    useEffect(() => {
        if (!open) return
        setSelected(currentLayoutUid || NONE_VALUE)
        setError(null)
    }, [open, currentLayoutUid])

    const handleSave = useCallback(async () => {
        setError(null)
        const layoutUid = selected === NONE_VALUE ? null : selected
        try {
            await updateAsset.mutateAsync({ document_folder_layout_uid: layoutUid })
            refreshAssetDocumentFolders(queryClient, org, assetUid)
            onOpenChange(false)
        } catch (err: unknown) {
            // 422 payloads are {title, code, fields:[{name, message}]} — surface
            // the document_folder_layout_uid message verbatim when present (it
            // is what a race turning the asset non-site-level looks like).
            const payload = err as { fields?: Array<{ name?: string; message?: string }>; title?: string; message?: string }
            const fieldError = payload?.fields?.find(f => f.name === 'document_folder_layout_uid')?.message
            setError(fieldError || payload?.fields?.[0]?.message || payload?.title || payload?.message || t('asset_view.layout_dialog.error'))
        }
    }, [selected, updateAsset, queryClient, org, assetUid, onOpenChange, t])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="asset-folder-layout-dialog">
                <DialogHeader>
                    <DialogTitle>{t('asset_view.layout_dialog.title')}</DialogTitle>
                    <DialogDescription>{t('asset_view.layout_dialog.description')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="asset-folder-layout-select">
                            {t('asset_view.layout_dialog.select_label')}
                        </Label>
                        <div className="flex gap-2">
                            <Select value={selected} onValueChange={setSelected} disabled={isLoading}>
                                <SelectTrigger id="asset-folder-layout-select" className="flex-1" data-testid="asset-folder-layout-select">
                                    <SelectValue />
                                </SelectTrigger>
                                {/*
                                  * Capped and scrollable. An org can hold dozens of
                                  * layouts and the shared SelectContent has no
                                  * height bound, so the popper grows past the
                                  * viewport and its end options become unclickable.
                                  * The height has to be forced on the Radix
                                  * viewport (the direct child), which the shared
                                  * component pins to the trigger height with
                                  * `overflow: visible` — capping the content alone
                                  * would clip those options instead of scrolling
                                  * to them.
                                  */}
                                <SelectContent className="max-h-72 [&>div]:!h-auto [&>div]:max-h-[17rem] [&>div]:overflow-y-auto">
                                    <SelectItem value={NONE_VALUE} data-testid="asset-folder-layout-option-none">
                                        {t('asset_view.layout_dialog.inherit_option')}
                                    </SelectItem>
                                    {layouts.map(layout => (
                                        <SelectItem
                                            key={layout.uid}
                                            value={layout.uid as string}
                                            data-testid={`asset-folder-layout-option-${layout.code}`}
                                        >
                                            <span>{getLocalizedName(layout.name as Record<string, string>, i18n.language) || layout.code}</span>
                                            <span className="ml-2 text-xs text-muted-foreground">{layout.code}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                asChild={selected !== NONE_VALUE}
                                variant="outline"
                                size="icon"
                                disabled={selected === NONE_VALUE}
                                aria-label={t('asset_view.layout_dialog.edit_layout')}
                                title={t('asset_view.layout_dialog.edit_layout')}
                                data-testid="asset-folder-layout-edit-btn"
                            >
                                {selected === NONE_VALUE ? (
                                    <AppIcon path={mdiPencilOutline} size="sm" />
                                ) : (
                                    <Link to="/$org/folder-layouts/$layoutUid" params={{ org, layoutUid: selected }}>
                                        <AppIcon path={mdiPencilOutline} size="sm" />
                                    </Link>
                                )}
                            </Button>
                        </div>
                    </div>

                    {hasDescendants && (
                        <p
                            className="flex items-start gap-2 text-xs text-muted-foreground"
                            data-testid="asset-folder-layout-subtree-warning"
                        >
                            <AppIcon path={mdiAlertOutline} size="sm" className="mt-0.5 shrink-0" />
                            <span>{t('asset_view.layout_dialog.subtree_warning')}</span>
                        </p>
                    )}

                    {error && (
                        <p
                            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                            role="alert"
                            data-testid="asset-folder-layout-error"
                        >
                            {error}
                        </p>
                    )}
                </div>

                <DialogFooter className="sm:justify-between">
                    <Link
                        to="/$org/folder-layouts"
                        params={{ org }}
                        className="inline-flex items-center gap-1 self-center text-xs text-muted-foreground underline-offset-2 hover:underline"
                        data-testid="asset-folder-layout-manage-link"
                    >
                        <AppIcon path={mdiOpenInNew} size="xs" />
                        {t('asset_view.layout_dialog.manage_link')}
                    </Link>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="asset-folder-layout-cancel"
                        >
                            {t('asset_view.layout_dialog.cancel')}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={updateAsset.isPending}
                            data-testid="asset-folder-layout-save"
                        >
                            {t('asset_view.layout_dialog.save')}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
